import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Crown, ChevronRight, Award, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const VIPCtaSection = ({ onBookNow }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <section className="relative py-16 sm:py-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Card className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden">
          <CardContent className="p-8 sm:p-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/30 mb-6">
              <Crown className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-400 text-sm font-semibold">{t("home_cta.vip_badge")}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">{t("home_cta.vip_title")}</h2>
            <p className="text-slate-400 mb-8 max-w-xl mx-auto">
              {t("home_cta.vip_sub")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                onClick={() => navigate("/vip-signup")}
                className="w-full sm:w-auto h-12 px-8 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-full shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 hover:scale-105 transition-all duration-300"
                data-testid="join-vip-cta"
              >
                {t("home_cta.join_vip")}
                <Crown className="w-5 h-5 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={onBookNow}
                className="w-full sm:w-auto h-12 px-8 border-white/10 text-white hover:bg-white/5 rounded-full"
                data-testid="book-now-cta"
              >
                {t("home_cta.book_without_vip")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

const VendorCtaSection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <section className="relative py-16 sm:py-20 bg-gradient-to-b from-transparent to-emerald-500/5">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Card className="bg-gradient-to-br from-emerald-900/30 to-teal-900/30 backdrop-blur-xl border border-emerald-500/20 rounded-3xl overflow-hidden">
          <CardContent className="p-8 sm:p-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-6">
              <Award className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 text-sm font-semibold">{t("home_cta.vendor_badge")}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">{t("home_cta.vendor_title")}</h2>
            <p className="text-slate-300 mb-6 max-w-xl mx-auto">
              {t("home_cta.vendor_sub")}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6 mb-8 text-sm">
              {[t("home_cta.benefit_schedule"), t("home_cta.benefit_split"), t("home_cta.benefit_payments")].map((text) => (
                <div key={text} className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span className="text-slate-300">{text}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                onClick={() => navigate("/vendor-onboarding")}
                className="w-full sm:w-auto h-12 px-8 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-full shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 hover:scale-105 transition-all duration-300"
                data-testid="earn-with-washpros-cta"
              >
                {t("home_cta.start_earning")}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/vendors/apply")}
                className="w-full sm:w-auto h-12 px-8 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 rounded-full"
                data-testid="apply-as-contractor"
              >
                {t("home_cta.apply_gig")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export { VIPCtaSection, VendorCtaSection };
